import { getLstToken, getLstPhone } from "@/utils/lstSessionStorage";
import { showToast } from "vant";
import { getUserInfo, getSyntheticGoodsList, getMaxSyntheticNum, getAllUnsoldGoods, syntheticNewGoods, getRecastGoodsList, recastNewGoods, getHomeActivity, getSyntheticCutDetail, syntheticCutJoin } from "@/api/lst";
const ASSIST_LOOP_MAX = 500;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
function getAuth() {
    return {
        token: getLstToken(),
        phone: getLstPhone()
    };
}
function parsePositiveInt(v) {
    const n = Number(v);
    if (!Number.isFinite(n) || n < 1)
        return null;
    return Math.min(Math.floor(n), 500);
}
function burnSlotsPerAlbum(al, group, albumLen) {
    const keys = ["burnNum", "needNum", "burnNumber", "num", "count", "selectNum", "goodsNum", "needGoodsNum"];
    for (const k of keys) {
        const hit = parsePositiveInt(al[k]);
        if (hit != null)
            return hit;
    }
    if (albumLen === 1 && group) {
        for (const k of ["selectNum", "needSelectNum", "totalBurnNum", "burnTotal", "needNum"]) {
            const hit = parsePositiveInt(group[k]);
            if (hit != null)
                return hit;
        }
    }
    return 1;
}
function effectiveNeedOwnListCompose(detail) {
    const raw = detail?.needOwnList || [];
    const own = detail?.ownList || [];
    const out = [];
    for (const group of raw) {
        let albums = [...group.albumList || []];
        const needSel = parsePositiveInt(group.selectNum);
        if (!albums.length && needSel != null) {
            const ownG = own.find((o) => String(o.groupId) === String(group.groupId));
            for (const al of ownG?.albumList || []) {
                albums.push({
                    albumId: al.albumId,
                    goodsName: al.goodsName || "\u6750\u6599",
                    goodsPic: al.goodsPic,
                    goodsList: Array.isArray(al.goodsList) ? al.goodsList : [],
                    sellLimit: al.sellLimit
                });
            }
        }
        const ownG = own.find((o) => String(o.groupId) === String(group.groupId));
        albums = albums.map((al) => {
            const ownAl = ownG?.albumList?.find((x) => String(x.albumId) === String(al.albumId));
            return {
                ...al,
                sellLimit: al.sellLimit != null && al.sellLimit !== "" ? al.sellLimit : ownAl?.sellLimit
            };
        });
        out.push({ ...group, albumList: albums });
    }
    return out;
}
function effectiveNeedOwnListRecast(detail) {
    const raw = detail?.needOwnList || [];
    const out = [];
    for (const group of raw) {
        let albums = [...group.albumList || []];
        const needSel = parsePositiveInt(group.selectNum);
        if (!albums.length && needSel != null) {
            const ownG = (detail?.ownList || []).find((o) => String(o.groupId) === String(group.groupId));
            for (const al of ownG?.albumList || []) {
                albums.push({
                    albumId: al.albumId,
                    goodsName: al.goodsName || "\u6750\u6599",
                    goodsPic: al.goodsPic,
                    goodsList: Array.isArray(al.goodsList) ? al.goodsList : [],
                    sellLimit: al.sellLimit
                });
            }
        }
        out.push({ ...group, albumList: albums });
    }
    return out;
}
function materialSlotsFromEffective(effective) {
    const out = [];
    for (const group of effective) {
        const list = group.albumList || [];
        let seq = 0;
        for (const al of list) {
            const n = burnSlotsPerAlbum(al, group, list.length);
            const baseName = al.goodsName || "\u6750\u6599";
            const sellLimit = al.sellLimit != null && al.sellLimit !== "" ? String(al.sellLimit) : "";
            for (let i = 0; i < n; i++) {
                out.push({
                    key: `${group.groupId}-${al.albumId}-${seq}`,
                    groupId: group.groupId,
                    albumId: al.albumId,
                    goodsName: n > 1 ? `${baseName}\uFF08${i + 1}/${n}\uFF09` : baseName,
                    goodsPic: al.goodsPic,
                    sellLimit
                });
                seq += 1;
            }
        }
    }
    return out;
}
function mergeOwnListIntoGoodsCache(detail, goodsCache) {
    for (const grp of detail?.ownList || []) {
        for (const al of grp.albumList || []) {
            const aid = al.albumId;
            const map = new Map((goodsCache[aid] || []).map((g) => [String(g.gId), { ...g }]));
            for (const og of al.goodsList || []) {
                if (Number(og.onSale) === 6)
                    continue;
                const k = String(og.gId);
                if (!map.has(k)) {
                    map.set(k, {
                        gId: og.gId,
                        gNum: og.gNum,
                        onSale: og.onSale,
                        sellLimit: al.sellLimit,
                        thumbPic: og.thumbPic || al.goodsPic,
                        goodsPic: og.goodsPic || al.goodsPic,
                        headPic: og.headPic
                    });
                }
            }
            goodsCache[aid] = Array.from(map.values());
        }
    }
}
function applyDefaultPicksFromWarehouse(materialSlots, goodsCache) {
    const picked = {};
    if (!materialSlots.length)
        return picked;
    const used = new Set();
    for (const slot of materialSlots) {
        const pool = (goodsCache[slot.albumId] || []).filter((g) => Number(g.onSale) !== 6).sort((a, b) => Number(a.gNum) - Number(b.gNum));
        const pick = pool.find((g) => !used.has(String(g.gId)));
        if (pick) {
            picked[slot.key] = pick.gId;
            used.add(String(pick.gId));
        }
    }
    return picked;
}
function normalizeGId(gId) {
    const s = String(gId).trim();
    if (/^-?\d+$/.test(s)) {
        const n = Number(s);
        if (Number.isSafeInteger(n))
            return n;
    }
    return gId;
}
function buildComposeBurnGroup(detail, materialSlots, picked) {
    const needOwnList = detail?.needOwnList || [];
    const byGroup = new Map();
    for (const s of materialSlots) {
        if (!byGroup.has(s.groupId))
            byGroup.set(s.groupId, []);
        byGroup.get(s.groupId).push(s);
    }
    return needOwnList.map((group) => {
        const slots = byGroup.get(group.groupId) || [];
        const gIdList = slots.map((s) => picked[s.key]).filter((x) => x != null && x !== "").map(normalizeGId);
        const pointType = Object.prototype.hasOwnProperty.call(group, "pointType") ? group.pointType : null;
        const syntheticDeductList = Array.isArray(group.syntheticDeductList) ? group.syntheticDeductList : [];
        return {
            groupId: group.groupId != null ? String(group.groupId) : "",
            gIdList,
            pointType,
            syntheticDeductList
        };
    });
}
function buildRecastSubmitPayload(activityId, detail, materialSlots, picked, isExchangeFlow) {
    if (isExchangeFlow || Number(detail?.activityType) === 4) {
        const ids = materialSlots.map((s) => picked[s.key]).filter((x) => x != null && x !== "").map(normalizeGId);
        return { activityId: String(activityId), ids };
    }
    return { activityId: String(activityId), burnGroup: buildComposeBurnGroup(detail, materialSlots, picked) };
}
function needOwnListHasAlbums(data) {
    const list = data?.needOwnList;
    if (!Array.isArray(list))
        return false;
    return list.some((g) => Array.isArray(g.albumList) && g.albumList.length > 0);
}
async function mergeDetailWithSyntheticGoodsList(token, phone, activityId, recastData) {
    let merged = { ...recastData || {} };
    try {
        const synRes = await getSyntheticGoodsList(token, phone, activityId);
        if (synRes.code !== 1 || !synRes.data)
            return merged;
        const syn = synRes.data;
        if (Array.isArray(syn.ownPointList) && syn.ownPointList.length) {
            merged.ownPointList = syn.ownPointList;
        }
        if (!needOwnListHasAlbums(merged) && needOwnListHasAlbums(syn)) {
            merged.needOwnList = syn.needOwnList;
            if (syn.ownList != null) {
                const synOwnHasGoods = (syn.ownList || []).some((o) => (o.albumList || []).some((al) => (al.goodsList || []).length > 0));
                const mergedOwnHasGoods = (merged.ownList || []).some((o) => (o.albumList || []).some((al) => (al.goodsList || []).length > 0));
                if (synOwnHasGoods || !mergedOwnHasGoods) {
                    merged.ownList = syn.ownList;
                }
            }
        }
        if (Array.isArray(syn.burnGroupCutRspList) && syn.burnGroupCutRspList.length && !merged.burnGroupCutRspList?.length) {
            merged.burnGroupCutRspList = syn.burnGroupCutRspList;
        }
    }
    catch {
    }
    return merged;
}
function canSubmitSlots(materialSlots, picked) {
    if (!materialSlots.length)
        return false;
    return materialSlots.every((s) => !!picked[s.key]);
}
export async function runActivityAssistJob(job) {
    const { token, phone } = getAuth();
    if (!token || !phone) {
        showToast("\u8BF7\u5148\u767B\u5F55");
        return;
    }
    const routeName = job.targetRoute;
    if (routeName === "ComposeSynthetic") {
        await runComposeAssist(job);
    }
    else if (routeName === "RecastSynthetic" || routeName === "ExchangeSynthetic") {
        await runRecastAssist(job, routeName === "ExchangeSynthetic");
    }
    else if (routeName === "DecomposeSynthetic") {
        await runDecomposeAssist(job);
    }
}
async function runComposeAssist(job) {
    const { token, phone } = getAuth();
    const activityId = String(job.activityId);
    const uRes = await getUserInfo(token, phone);
    if (uRes.code !== 1) {
        showToast(uRes.message || "\u7528\u6237\u4FE1\u606F\u5931\u8D25");
        return;
    }
    const uid = uRes.data.userId;
    let detail = {};
    let picked = {};
    let goodsCache = {};
    const refreshCompose = async () => {
        const dRes = await getSyntheticGoodsList(token, phone, activityId);
        if (dRes.code !== 1)
            throw new Error(dRes.message || "\u52A0\u8F7D\u5931\u8D25");
        const payload = dRes.data || {};
        if (Number(payload.activityType) === 4) {
            throw new Error("\u6D3B\u52A8\u7C7B\u578B\u5DF2\u53D8\u66F4\uFF0C\u8BF7\u5728\u53D1\u73B0\u9875\u91CD\u8BD5");
        }
        const mRes = await getMaxSyntheticNum(token, phone, activityId);
        detail = payload;
        goodsCache = {};
        const effective = effectiveNeedOwnListCompose(detail);
        const materialSlots = materialSlotsFromEffective(effective);
        const albumIds = [...new Set(materialSlots.map((s) => s.albumId))];
        for (const aid of albumIds) {
            const list = await getAllUnsoldGoods(token, phone, aid, uid);
            goodsCache[aid] = (list || []).filter((g) => g.onSale !== 6);
        }
        mergeOwnListIntoGoodsCache(detail, goodsCache);
        picked = applyDefaultPicksFromWarehouse(materialSlots, goodsCache);
        return materialSlots;
    };
    let materialSlots;
    try {
        materialSlots = await refreshCompose();
    }
    catch (e) {
        showToast(e.message || "\u52A0\u8F7D\u5931\u8D25");
        return;
    }
    const maxIter = job.loopUntilStop ? ASSIST_LOOP_MAX : 1;
    let n = 0;
    for (let i = 0; i < maxIter; i++) {
        const effective = effectiveNeedOwnListCompose(detail);
        materialSlots = materialSlotsFromEffective(effective);
        if (!canSubmitSlots(materialSlots, picked)) {
            if (n === 0)
                showToast("\u6750\u6599\u4E0D\u8DB3\u6216\u6761\u4EF6\u4E0D\u6EE1\u8DB3\uFF0C\u52A9\u624B\u672A\u6267\u884C");
            else
                showToast(`\u5C0F\u52A9\u624B\u5DF2\u505C\u6B62\uFF1A\u5171\u5B8C\u6210 ${n} \u6B21\u5408\u6210`);
            break;
        }
        const payload = { activityId, burnGroup: buildComposeBurnGroup(detail, materialSlots, picked) };
        const res = await syntheticNewGoods(token, phone, payload);
        if (res.code !== 1) {
            showToast(res.message || "\u5408\u6210\u5931\u8D25\uFF0C\u52A9\u624B\u505C\u6B62");
            break;
        }
        n++;
        try {
            materialSlots = await refreshCompose();
        }
        catch (e) {
            showToast(e.message || "\u5237\u65B0\u5931\u8D25");
            break;
        }
        await sleep(380);
    }
    if (job.loopUntilStop && n >= ASSIST_LOOP_MAX)
        showToast("\u5DF2\u8FBE\u5355\u6B21\u52A9\u624B\u4E0A\u9650\uFF0C\u5DF2\u505C\u6B62");
    else if (!job.loopUntilStop && n > 0)
        showToast(`\u9884\u7EA6\u5230\u70B9\uFF1A\u5DF2\u5B8C\u6210 ${n} \u6B21\u5408\u6210`);
}
async function runRecastAssist(job, isExchangeRoute) {
    const { token, phone } = getAuth();
    const activityId = String(job.activityId);
    const groupId = job.groupId != null ? String(job.groupId) : "";
    const uRes = await getUserInfo(token, phone);
    if (uRes.code !== 1) {
        showToast(uRes.message || "\u7528\u6237\u4FE1\u606F\u5931\u8D25");
        return;
    }
    const uid = uRes.data.userId;
    let detail = {};
    let picked = {};
    let goodsCache = {};
    const loadRecast = async (forceAutoPick) => {
        const dRes = await getRecastGoodsList(token, phone, activityId);
        if (dRes.code !== 1) {
            throw new Error(dRes.message || "\u52A0\u8F7D\u5931\u8D25");
        }
        let data = dRes.data || {};
        const isExchangeFlow = isExchangeRoute || Number(data.activityType) === 4;
        if (!isExchangeFlow) {
            data = await mergeDetailWithSyntheticGoodsList(token, phone, activityId, data);
        }
        detail = data;
        goodsCache = {};
        const effective = effectiveNeedOwnListRecast(detail);
        const materialSlots = materialSlotsFromEffective(effective);
        const albumIds = [...new Set(materialSlots.map((s) => s.albumId))];
        for (const aid of albumIds) {
            const list = await getAllUnsoldGoods(token, phone, aid, uid);
            goodsCache[aid] = (list || []).filter((g) => g.onSale !== 6);
        }
        mergeOwnListIntoGoodsCache(detail, goodsCache);
        if (isExchangeFlow || forceAutoPick) {
            picked = applyDefaultPicksFromWarehouse(materialSlots, goodsCache);
        }
        else {
            picked = {};
        }
        return materialSlots;
    };
    let materialSlots;
    try {
        materialSlots = await loadRecast(true);
    }
    catch (e) {
        showToast(e.message || "\u52A0\u8F7D\u5931\u8D25");
        return;
    }
    const maxIter = job.loopUntilStop ? ASSIST_LOOP_MAX : 1;
    let n = 0;
    const labelOk = isExchangeRoute || Number(detail.activityType) === 4 ? "\u5151\u6362" : "\u7F6E\u6362";
    for (let i = 0; i < maxIter; i++) {
        const effective = effectiveNeedOwnListRecast(detail);
        materialSlots = materialSlotsFromEffective(effective);
        const isExchangeFlow = isExchangeRoute || Number(detail.activityType) === 4;
        if (!canSubmitSlots(materialSlots, picked)) {
            if (n === 0)
                showToast("\u6750\u6599\u4E0D\u8DB3\u6216\u6761\u4EF6\u4E0D\u6EE1\u8DB3\uFF0C\u52A9\u624B\u672A\u6267\u884C");
            else
                showToast(`\u5C0F\u52A9\u624B\u5DF2\u505C\u6B62\uFF1A\u5171\u5B8C\u6210 ${n} \u6B21${labelOk}`);
            break;
        }
        const payload = buildRecastSubmitPayload(activityId, detail, materialSlots, picked, isExchangeFlow);
        const res = await recastNewGoods(token, phone, payload);
        if (res.code !== 1) {
            showToast(res.message || `${labelOk}\u5931\u8D25\uFF0C\u52A9\u624B\u505C\u6B62`);
            break;
        }
        n++;
        if (isExchangeFlow && groupId) {
            try {
                const homeRes = await getHomeActivity(token, phone, groupId, isExchangeRoute ? "exchange" : "recast");
                if (homeRes.code === 1) {
                    const row = (homeRes.data?.activityList || []).find((a) => String(a.id) === String(activityId));
                    if (!row || Number(row.userCondition) !== 1) {
                        showToast("\u5DF2\u65E0\u5151\u6362\u8D44\u683C\uFF0C\u52A9\u624B\u505C\u6B62");
                        break;
                    }
                }
            }
            catch {
            }
        }
        try {
            materialSlots = await loadRecast(true);
        }
        catch (e) {
            showToast(e.message || "\u5237\u65B0\u5931\u8D25");
            break;
        }
        await sleep(380);
    }
    if (job.loopUntilStop && n >= ASSIST_LOOP_MAX)
        showToast("\u5DF2\u8FBE\u5355\u6B21\u52A9\u624B\u4E0A\u9650\uFF0C\u5DF2\u505C\u6B62");
    else if (!job.loopUntilStop && n > 0)
        showToast(`\u9884\u7EA6\u5230\u70B9\uFF1A\u5DF2\u5B8C\u6210 ${n} \u6B21${labelOk}`);
}
async function runDecomposeAssist(job) {
    const { token, phone } = getAuth();
    const activityId = String(job.activityId);
    const uRes = await getUserInfo(token, phone);
    if (uRes.code !== 1) {
        showToast(uRes.message || "\u7528\u6237\u4FE1\u606F\u5931\u8D25");
        return;
    }
    const uid = uRes.data.userId;
    let detail = {};
    let picked = {};
    let goodsCache = {};
    const loadDecompose = async () => {
        const dRes = await getSyntheticCutDetail(token, phone, { activityId });
        if (dRes.code !== 1) {
            throw new Error(dRes.message || "\u52A0\u8F7D\u5931\u8D25");
        }
        let data = dRes.data || {};
        data = await mergeDetailWithSyntheticGoodsList(token, phone, activityId, data);
        detail = data;
        goodsCache = {};
        const effective = effectiveNeedOwnListRecast(detail);
        const materialSlots = materialSlotsFromEffective(effective);
        const albumIds = [...new Set(materialSlots.map((s) => s.albumId))];
        for (const aid of albumIds) {
            const list = await getAllUnsoldGoods(token, phone, aid, uid);
            goodsCache[aid] = (list || []).filter((g) => g.onSale !== 6);
        }
        mergeOwnListIntoGoodsCache(detail, goodsCache);
        picked = applyDefaultPicksFromWarehouse(materialSlots, goodsCache);
        return materialSlots;
    };
    let materialSlots;
    try {
        materialSlots = await loadDecompose();
    }
    catch (e) {
        showToast(e.message || "\u52A0\u8F7D\u5931\u8D25");
        return;
    }
    const maxIter = job.loopUntilStop ? ASSIST_LOOP_MAX : 1;
    let n = 0;
    const labelOk = "\u5206\u89E3";
    for (let i = 0; i < maxIter; i++) {
        const effective = effectiveNeedOwnListRecast(detail);
        materialSlots = materialSlotsFromEffective(effective);
        if (!canSubmitSlots(materialSlots, picked)) {
            if (n === 0)
                showToast("\u6750\u6599\u4E0D\u8DB3\u6216\u6761\u4EF6\u4E0D\u6EE1\u8DB3\uFF0C\u52A9\u624B\u672A\u6267\u884C");
            else
                showToast(`\u5C0F\u52A9\u624B\u5DF2\u505C\u6B62\uFF1A\u5171\u5B8C\u6210 ${n} \u6B21${labelOk}`);
            break;
        }
        const payload = buildRecastSubmitPayload(activityId, detail, materialSlots, picked, false);
        const res = await syntheticCutJoin(token, phone, payload);
        if (res.code !== 1) {
            showToast(res.message || `${labelOk}\u5931\u8D25\uFF0C\u52A9\u624B\u505C\u6B62`);
            break;
        }
        n++;
        try {
            materialSlots = await loadDecompose();
        }
        catch (e) {
            showToast(e.message || "\u5237\u65B0\u5931\u8D25");
            break;
        }
        await sleep(380);
    }
    if (job.loopUntilStop && n >= ASSIST_LOOP_MAX)
        showToast("\u5DF2\u8FBE\u5355\u6B21\u52A9\u624B\u4E0A\u9650\uFF0C\u5DF2\u505C\u6B62");
    else if (!job.loopUntilStop && n > 0)
        showToast(`\u9884\u7EA6\u5230\u70B9\uFF1A\u5DF2\u5B8C\u6210 ${n} \u6B21${labelOk}`);
}
